import { safe_get } from "../../../report-visuals";
import { emptyChartConfiguration, type TARSBarChartTableData } from "./tarsBarChartHelpers";
import { normalizeChartType, TARS_COLUMN_CHART } from "../notebook-details/analytics/facet-chart-types";
import type { FacetSnapshotForUpdate } from "../notebook-details/analytics/facet-workspace-update-helpers";
import { facetResultFromExecuteResponse } from "../notebook-details/analytics/facet-etl-execute-helpers";
import { ensureChartEntryId } from "./facet-chart-publish-helpers";

export function isChartTablePayload(v: unknown): v is TARSBarChartTableData {
    if (v == null || typeof v !== "object") return false;
    const o = v as Record<string, unknown>;
    return Array.isArray(o.rows) && Array.isArray(o.headers);
}

export type NormalizeChartEntryResult =
    | { ok: true; entry: Record<string, unknown> }
    | { ok: false; error: string };

/** Defaults first, then saved `configuration` (invalid / missing → defaults only). */
function mergePersistedChartConfiguration(configNode: unknown): Record<string, unknown> {
    const base = emptyChartConfiguration() as unknown as Record<string, unknown>;
    if (configNode != null && typeof configNode === "object" && !Array.isArray(configNode)) {
        return { ...base, ...(configNode as Record<string, unknown>) };
    }
    return { ...base };
}

/** Build a persisted chart object from user JSON + facet defaults. */
export function normalizeChartEntryFromParsed(
    parsed: Record<string, unknown>,
    facet: FacetSnapshotForUpdate,
    fallbackType?: string
): NormalizeChartEntryResult {
    const normalizedFacetResult = facetResultFromExecuteResponse(parsed);
    if (!isChartTablePayload(normalizedFacetResult)) {
        return { ok: false, error: "Chart JSON must include rows and headers/col_header arrays" };
    }
    const parsedType = safe_get(parsed, "type", undefined);
    const typeRaw = typeof parsedType === "string" ? parsedType : (fallbackType ?? TARS_COLUMN_CHART);
    const type = normalizeChartType(typeRaw);
    const moreNameFallback = safe_get(facet, "more.name", undefined);
    const parsedTitle = safe_get(parsed, "title", undefined);
    const title =
        typeof parsedTitle === "string" && parsedTitle.trim() !== ""
            ? parsedTitle
            : ((typeof moreNameFallback === "string" && moreNameFallback.trim() !== ""
                  ? moreNameFallback
                  : (facet?.name ?? "Untitled")) ?? "");
    const configNode = safe_get(parsed, "configuration", undefined);
    const configuration = mergePersistedChartConfiguration(configNode);
    const chartEntry: Record<string, unknown> = {
        type,
        title,
        configuration,
        rows: normalizedFacetResult.rows,
        headers: normalizedFacetResult.headers,
    };
    const idNode = safe_get(parsed, "id", undefined);
    if (typeof idNode === "string" && idNode.trim() !== "") {
        chartEntry.id = idNode.trim();
    }
    ensureChartEntryId(chartEntry);
    const imageNode = safe_get(parsed, "image", undefined);
    if (imageNode != null && typeof imageNode === "object" && !Array.isArray(imageNode)) {
        chartEntry.image = imageNode;
    }
    const colorsNode = safe_get(parsed, "colors", undefined);
    if (Array.isArray(colorsNode)) {
        chartEntry.colors = colorsNode;
    }
    const seriesColorsNode = safe_get(parsed, "seriesColors", undefined);
    if (Array.isArray(seriesColorsNode)) {
        chartEntry.seriesColors = seriesColorsNode;
    }
    return { ok: true, entry: chartEntry };
}
